import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Points } from 'components/Points/Points';
import GlassContainer from 'components/GlassContainer/GlassContainer';
import { FoilCardImage } from 'components/FoilCardImage';
import { WantListCardOverlay } from 'screens/Public/UserProfile/components/ProfileWantListTab/WantListCardOverlay';
import SendCardTooltip from 'screens/Main/tabs/Send/components/SendCardTooltip';
import { SetIcon } from 'components/SetIcon';
import { useWindowSize } from 'hooks/useWindowSize';
import { AssetUrls } from 'config/assets';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import { TradeAlert } from 'store/services/tradeAlerts/tradeAlertsApi';
import { TradeCard } from 'store/services/trade/tradeApi';
import './AlertCard.css';

// Inline SVG card-back placeholder — never depends on S3/CDN, can't 403
const CARD_BACK_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='223' height='311' viewBox='0 0 223 311'%3E%3Crect width='223' height='311' rx='12' fill='%231a1a2e'/%3E%3Crect x='8' y='8' width='207' height='295' rx='8' fill='none' stroke='%236F51E4' stroke-width='2' opacity='0.4'/%3E%3Ctext x='111.5' y='165' text-anchor='middle' fill='%236F51E4' font-size='48' opacity='0.5'%3E%E2%9C%A6%3C/text%3E%3C/svg%3E";

// Build the ordered fallback chain (CDN size variations + scryfall + inline back).
const buildFallbacks = (alert: TradeAlert): string[] => {
  const fallbacks: string[] = [];
  const url = alert.card.imageUrl;
  if (url) {
    if (url.includes('/normal/')) {
      fallbacks.push(url.replace('/normal/', '/small/'));
      fallbacks.push(url.replace('/normal/', '/large/'));
    } else if (url.includes('/large/')) {
      fallbacks.push(url.replace('/large/', '/normal/'));
      fallbacks.push(url.replace('/large/', '/small/'));
    } else if (url.includes('/small/')) {
      fallbacks.push(url.replace('/small/', '/normal/'));
    }
  }
  if (alert.card.scryfallId) {
    fallbacks.push(AssetUrls.cards.getCardImage(alert.card.scryfallId, 'normal'));
    fallbacks.push(AssetUrls.cards.getCardImage(alert.card.scryfallId, 'small'));
  }
  return fallbacks;
};

interface AlertCardTileProps {
  alert: TradeAlert;
  /** Which tab the tile is being shown in — controls the dismiss vs restore icon. */
  variant: 'all' | 'hidden';
  onSend: (alert: TradeAlert) => void;
  onHide: (alert: TradeAlert) => void;
  onUnhide: (alert: TradeAlert) => void;
  /**
   * Requester identity block, rendered between the card meta and the Send button.
   * Passed in by-card mode; omitted in by-trader mode (the group renders it once).
   */
  profile?: React.ReactNode;
  /** True when rendered inside an AlertTradeGroup — applies the lighter tile chrome. */
  grouped?: boolean;
}

/**
 * One card's worth of an alert: image, value pill, name/meta, relative-time + dismiss
 * overlays, and a per-card Send button. The requester identity is supplied via the
 * optional `profile` slot so this tile serves both the standalone by-card card and the
 * grouped by-trader tile (where the profile lives once in the group header).
 */
export const AlertCardTile: React.FC<AlertCardTileProps> = ({
  alert,
  variant,
  onSend,
  onHide,
  onUnhide,
  profile,
  grouped,
}) => {
  // Track the fallback step for this card's single image. useRef, NOT dataset —
  // React re-renders recreate the DOM element and would reset dataset values.
  const fallbackStepRef = useRef<number>(0);
  const fallbacksRef = useRef<string[]>(buildFallbacks(alert));

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const step = fallbackStepRef.current;
    fallbackStepRef.current = step + 1;
    const fallbacks = fallbacksRef.current;
    if (step < fallbacks.length) {
      e.currentTarget.src = fallbacks[step];
      return;
    }
    e.currentTarget.src = CARD_BACK_DATA_URI;
  };

  const { card } = alert;
  const metaParts = [card.setCode?.toUpperCase(), card.collectorNumber && `#${card.collectorNumber}`]
    .filter(Boolean)
    .join(' ');
  const setIconUrl = card.setCode
    ? `https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons/${card.setCode.toLowerCase()}.svg`
    : null;

  // Card-image hover affordances (profile Want-List convention): the WantListCardOverlay
  // (Beleren name link + ↗ + Add to Trade) over the image, plus the SendCardTooltip
  // ("username wants:" accepted criteria) at the card's right edge. Desktop-only.
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const [tooltipRect, setTooltipRect] = useState<{ left: number; right: number; top: number } | null>(null);
  const showHover = !isMobile && tooltipRect !== null;

  const handleImageEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMobile) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipRect({ left: rect.left, right: rect.right, top: rect.top });
  };
  const handleImageLeave = () => setTooltipRect(null);

  // SendCardTooltip consumes a TradeCard — build a fully-typed one from the alert so the
  // accepted sets/finishes/languages/conditions render directly (no cast, no derivation).
  const tooltipCard: TradeCard = {
    id: alert.id,
    cardId: card.id,
    name: card.name,
    set: card.setCode,
    setCode: card.setCode,
    condition: card.condition,
    language: card.language,
    foil: card.foil,
    edition: '',
    imageUrl: card.imageUrl,
    marketPrice: 0,
    pointValue: alert.pointValue,
    quantity: 1,
    wantListId: alert.wantListItemId,
    scryfallId: card.scryfallId,
    acceptableSetDetails: card.acceptableSetDetails,
    acceptableConditions: card.acceptableConditions,
    acceptableLanguages: card.acceptableLanguages,
    acceptFoil: card.acceptFoil,
    acceptNonFoil: card.acceptNonFoil,
    availableInFoil: card.availableInFoil,
    availableInNonFoil: card.availableInNonFoil,
    availableLanguages: card.availableLanguages,
  };

  const tileBody = (
    <>
      {/* Relative time — top-left, opposite the dismiss/restore control. In grouped
          mode it's omitted (redundant with the group header's most-recent time). */}
      {!grouped && (
        <span className="alert-card-time">{formatRelativeTime(alert.availableSince)}</span>
      )}

      {/* Dismiss / restore */}
      {variant === 'all' ? (
        <button
          className="alert-card-dismiss"
          aria-label="Hide alert"
          title="Hide alert"
          onClick={() => onHide(alert)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <button
          className="alert-card-dismiss"
          aria-label="Restore alert"
          title="Restore alert"
          onClick={() => onUnhide(alert)}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 14L4 9l5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 9h11a5 5 0 0 1 0 10h-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Card image (5/7 aspect ratio) */}
      <div
        className="alert-card-image"
        onMouseEnter={handleImageEnter}
        onMouseLeave={handleImageLeave}
      >
        <FoilCardImage
          src={card.imageUrl || CARD_BACK_DATA_URI}
          alt={card.name}
          foil={card.foil}
          onError={handleImageError}
          loading="lazy"
          borderRadius="8px"
        />
        {/* Profile Want-List overlay: Beleren name link + ↗ + Add to Trade. */}
        {showHover && (
          <WantListCardOverlay
            cardName={card.name}
            quantity={1}
            addedCount={0}
            onViewCard={() => navigate(buildCardUrl(card))}
            onAddToTrade={() => onSend(alert)}
          />
        )}
      </div>

      {/* Card value — centered directly below the card image. */}
      <div className="alert-card-value">
        <Points points={alert.pointValue} type="big" />
      </div>

      {/* Card name (links to card details) + metadata */}
      <Link className="alert-card-name" to={buildCardUrl(card)} title={card.name}>
        {card.name}
      </Link>
      <div className="alert-card-meta">
        {setIconUrl && (
          <SetIcon setIconUrl={setIconUrl} setName={card.setCode} size="sm" className="alert-card-set-icon" />
        )}
        <span>{metaParts}</span>
        {card.foil && <span className="alert-card-foil"> · Foil</span>}
      </div>

      {/* Requester identity (by-card mode only — grouped mode renders it in the header) */}
      {profile && <div className="alert-card-profile">{profile}</div>}

      {/* Send */}
      <button className="alert-card-send" onClick={() => onSend(alert)}>
        Send
      </button>

      {/* "username wants:" criteria tooltip — drawer flush with the card's right (or left,
          when flipped) edge, 16px below its top. */}
      {showHover && tooltipRect && (
        <SendCardTooltip
          cardData={tooltipCard}
          triggerRect={tooltipRect}
          username={alert.requester.username}
          onClose={handleImageLeave}
          onSetHover={() => {}}
          hoveredSetCode={null}
        />
      )}
    </>
  );

  // Standalone (By Card) tiles get the Card Details frosted-glass chrome. Grouped tiles stay
  // chrome-less — they sit inside the glass package container, so wrapping them too would
  // double-stack backdrop-filters.
  return grouped ? (
    <div className="alert-card alert-card--grouped">{tileBody}</div>
  ) : (
    <GlassContainer variant="cardDetails" className="alert-card">
      {tileBody}
    </GlassContainer>
  );
};

export default AlertCardTile;
